"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 4: MMC3 (TxROM)
 * ------------------------------------------------------------------ *
 * Register behaviour follows https://www.nesdev.org/wiki/MMC3 :
 * - $8000 selects the bank register for the next $8001 write; its bits 6/7
 *   are the PRG and CHR map modes and take effect on the $8000 write itself.
 * - R6/R7 ignore the top two bits (six PRG address lines).
 * - $C000 is the IRQ *latch*: it never touches the running counter.
 * - $C001 clears the counter and asks for a reload at the next A12 rise.
 * - The counter reloads from the latch when it is zero (or a reload was
 *   requested), otherwise it decrements; an enabled counter at zero raises
 *   the IRQ. Between IRQs that is exactly latch+1 filtered A12 rises.
 *
 * Used by Super Mario Bros. 3, Kirby's Adventure, Mega Man 3...
 * Fine-grained bank switching: two 8 KB switchable PRG-ROM banks, two 2 KB +
 * four 1 KB CHR banks, plus the scanline-counting IRQ for split-screen.
 */

function register(Mappers) {
  Mappers[4] = function (nes) {
    this.nes = nes;

    this.CMD_SEL_2_1K_VROM_0000 = 0;
    this.CMD_SEL_2_1K_VROM_0800 = 1;
    this.CMD_SEL_1K_VROM_1000 = 2;
    this.CMD_SEL_1K_VROM_1400 = 3;
    this.CMD_SEL_1K_VROM_1800 = 4;
    this.CMD_SEL_1K_VROM_1C00 = 5;
    this.CMD_SEL_ROM_PAGE1 = 6;
    this.CMD_SEL_ROM_PAGE2 = 7;

    // Seuil du filtre qui precede le compteur d'IRQ.
    // A12 sort de la PPU dans un etat qu'un filtre RC a amortit avant
    // d'horodater le compteur : un front montant ne compte que si le signal est
    // reste bas assez longtemps pour recharger le condensateur. nesdev parle de
    // trois fronts descendants de M2, soit six dots, et la plupart des
    // emulateurs prennent 8. On garde cette valeur parce qu'elle distingue les
    // deux cas qui comptent : le front normal de la fenetre des sprites, qui
    // arrive apres 256 dots au niveau bas, passe ; les rafales breves dues aux
    // ecritures $2006/$2007 sur de la CHR-RAM, non.
    this.A12_FILTER_DOTS = 8;
  };

  Mappers[4].prototype = new Mappers[0]();

  Mappers[4].prototype.irqClockRate = "a12";
  Mappers[4].prototype.watchesA12 = true;

  Mappers[4].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // Command register (written at $8000): selects which bank register to
    // update on the next $8001 write.
    this.command = 0;

    // PRG banking mode (bit 6 of $8000):
    //   0: $8000=R6, $A000=R7, $C000=second-to-last, $E000=last
    //   1: $8000=second-to-last, $A000=R7, $C000=R6, $E000=last
    this.prgAddressSelect = 0;

    // CHR banking mode (bit 7 of $8000):
    //   0: two 2 KB banks at $0000/$0800, four 1 KB banks at $1000-$1C00
    //   1: four 1 KB banks at $0000-$0C00, two 2 KB banks at $1000/$1800
    this.chrAddressSelect = 0;

    // Bank registers R0-R7 (raw values as written to $8001).
    // R0-R5: CHR banks (2 KB pair for R0/R1, 1 KB for R2-R5)
    // R6/R7: PRG banks (8 KB each, top two bits ignored by the chip)
    this.bankSelect = [0, 0, 0, 0, 0, 0, 0, 0];

    // IRQ state, the nesdev model:
    //   irqLatch   - $C000, the reload value
    //   irqCounter - the running counter, cleared by $C001
    //   irqReload  - set by $C001, consumed by the next A12 rise
    //   irqEnabled - $E001 sets it, $E000 clears it; firing clears it too
    //   irqPending - records an IRQ raised but not yet acknowledged
    this.irqCounter = 0;
    this.irqLatch = 0;
    this.irqReload = false;
    this.irqEnabled = false;
    this.irqPending = false;

    // Track last A12 state for IRQ clocking on rising edges.
    this.lastA12 = false;

    // PRG RAM protect ($A001): bit 1 active high blocks writes, bit 0 is the
    // chip enable (unused here: reads are not gated).
    this.prgRamEnabled = true;
    this.prgRamWritable = true;

    // Four-screen mirroring (TVROM/TQROM boards): the cartridge provides its
    // own 4 KB VRAM for four independent nametables. On these boards the
    // MMC3's $A000 mirroring register is not connected, so we must ignore it.
    this.fourScreen = this.nes && this.nes.rom && this.nes.rom.fourScreen;
    if (this.fourScreen) {
      this.ownsMirroring = true;
    }

    // Initialize mirroring from ROM header.
    if (this.nes && this.nes.rom) {
      this.setMirroring(this.nes.rom.getMirroringType());
    }
  };

  Mappers[4].prototype.write = function (address, value) {
    if (address < 0x8000) {
      if (address >= 0x6000 && address < 0x8000 && !this.prgRamWritable) {
        return;
      }
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    switch (address & 0xe001) {
      case 0x8000:
        // Bank select register ($8000).
        // Bits 0-2: register updated by the next $8001 write (0-7).
        // Bit 6: PRG map mode. Bit 7: CHR map mode (A12 inversion).
        // nesdev: "Bit 6 of the last value written to $8000 swaps the PRG
        // windows at $8000 and $C000" -- the modes act on the write itself,
        // not on the next bank-data write, so re-derive the windows here.
        this.command = value & 7;

        var prgMode = (value >> 6) & 1;
        if (prgMode !== this.prgAddressSelect) {
          this.prgAddressSelect = prgMode;
          this.updatePrgBanks();
        }

        var chrMode = (value >> 7) & 1;
        if (chrMode !== this.chrAddressSelect) {
          this.chrAddressSelect = chrMode;
          this.updateChrBanks();
        }
        break;

      case 0x8001:
        // Bank data register ($8001): raw value goes into the selected bank
        // register, then the affected window (or all of them, for PRG) is
        // re-derived from the registers.
        this.bankSelect[this.command] = value;
        if (this.command < this.CMD_SEL_ROM_PAGE1) {
          this.executeCommand(this.command, value);
        } else {
          this.updatePrgBanks();
        }
        break;

      case 0xa000:
        // Nametable arrangement ($A000): bit 0 = 0 selects the A10-wired
        // arrangement (CIRAM A10 = PPU A10), bit 0 = 1 the A11-wired one
        // (CIRAM A10 = PPU A11). The A11 (stacked $2000/$2800) arrangement is
        // what a vertically scrolling game like SMB3's world map needs.
        //
        // HORIZONTAL_MIRRORING is the stacked layout here -- the same constant
        // to layout mapping the reference jsnes uses -- so bit 0 set must go
        // there. This was inverted before, which left SMB3's map on the
        // side-by-side arrangement: the bottom screen's nametable writes
        // ($2800+) reached the wrong physical table and the HUD box at the
        // bottom of the map never rendered.
        //
        // On four-screen boards (TVROM, TQROM), the cartridge provides its own
        // 4 KB VRAM for four independent nametables. The MMC3's mirroring
        // register is not connected on these boards, so we ignore writes to it.
        if (!this.fourScreen) {
          if ((value & 1) !== 0) {
            this.setMirroring(this.nes.rom.HORIZONTAL_MIRRORING);
          } else {
            this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);
          }
        }
        break;

      case 0xa001:
        // PRG RAM protect: bit 1 = write protect (1 denies writes to
        // $6000-$7FFF), bit 0 = chip enable. Kirby's Adventure relies on the
        // write-protect bit; reading it the other way round silently threw
        // away the pointer table it keeps there and the CPU jumped to $0000.
        this.prgRamEnabled = (value & 1) !== 0;
        this.prgRamWritable = (value & 2) === 0;
        break;

      case 0xc000:
        // IRQ latch: "Writing to $C000 does not immediately affect the value
        // within the counter - this value is only used when the counter is
        // reloaded". Games update it from the IRQ handler to schedule the
        // next split, which only works if the running count is left alone.
        this.irqLatch = value;
        break;

      case 0xc001:
        // IRQ reload: clears the counter now, reloads from the latch at the
        // next rising edge of filtered A12.
        this.irqCounter = 0;
        this.irqReload = true;
        break;

      case 0xe000:
        // IRQ disable *and* acknowledge of any pending interrupt.
        this.irqEnabled = false;
        this.irqPending = false;
        break;

      case 0xe001:
        // IRQ enable; acknowledges too. Unconditional -- a pending reload
        // must not block arming, or the documented
        // $C000/$C001/$E001 sequence could never start a count.
        this.irqEnabled = true;
        this.irqPending = false;
        break;

      default:
      // Not an MMC3 register. Ignore it: games do probe $8000-$FFFF.
    }
  };

  // Applies a CHR bank register (commands 0-5) to the window the current
  // CHR map mode puts it in. The raw value is kept in bankSelect so a mode
  // change can re-derive every window from scratch.
  Mappers[4].prototype.executeCommand = function (cmd, arg) {
    switch (cmd) {
      case this.CMD_SEL_2_1K_VROM_0000:
        // Select 2 1KB VROM pages at 0x0000 (or 0x1000 in mode 1).
        // R0 ignores bit 0 (the MMC3 has no 1 KB resolution here).
        if (this.chrAddressSelect === 0) {
          this.load1kVromBank(arg & 0xfe, 0x0000);
          this.load1kVromBank((arg & 0xfe) + 1, 0x0400);
        } else {
          this.load1kVromBank(arg & 0xfe, 0x1000);
          this.load1kVromBank((arg & 0xfe) + 1, 0x1400);
        }
        break;

      case this.CMD_SEL_2_1K_VROM_0800:
        // Select 2 1KB VROM pages at 0x0800 (or 0x1800 in mode 1).
        // R1 ignores bit 0 as well.
        if (this.chrAddressSelect === 0) {
          this.load1kVromBank(arg & 0xfe, 0x0800);
          this.load1kVromBank((arg & 0xfe) + 1, 0x0c00);
        } else {
          this.load1kVromBank(arg & 0xfe, 0x1800);
          this.load1kVromBank((arg & 0xfe) + 1, 0x1c00);
        }
        break;

      case this.CMD_SEL_1K_VROM_1000:
        // Select 1K VROM page at 0x1000 (or 0x0000 in mode 1):
        if (this.chrAddressSelect === 0) {
          this.load1kVromBank(arg, 0x1000);
        } else {
          this.load1kVromBank(arg, 0x0000);
        }
        break;

      case this.CMD_SEL_1K_VROM_1400:
        if (this.chrAddressSelect === 0) {
          this.load1kVromBank(arg, 0x1400);
        } else {
          this.load1kVromBank(arg, 0x0400);
        }
        break;

      case this.CMD_SEL_1K_VROM_1800:
        if (this.chrAddressSelect === 0) {
          this.load1kVromBank(arg, 0x1800);
        } else {
          this.load1kVromBank(arg, 0x0800);
        }
        break;

      case this.CMD_SEL_1K_VROM_1C00:
        if (this.chrAddressSelect === 0) {
          this.load1kVromBank(arg, 0x1c00);
        } else {
          this.load1kVromBank(arg, 0x0c00);
        }
        break;

      default:
        // PRG commands (6/7) go through updatePrgBanks(), never here: a CHR
        // command must not be able to move a PRG window.
        break;
    }
  };

  // Re-derives all four PRG windows from the registers and the map mode.
  // R6/R7 ignore the top two bits: the MMC3 has only six PRG address lines.
  Mappers[4].prototype.updatePrgBanks = function () {
    var rom = this.nes.rom;
    if (rom.romCount === 0) {
      return;
    }
    var hardwired = (rom.romCount - 1) * 2; // second-last 8 KB bank
    var r6 = this.bankSelect[6] & 0x3f;
    var r7 = this.bankSelect[7] & 0x3f;

    if (this.prgAddressSelect === 0) {
      this.load8kRomBank(r6, 0x8000);
      this.load8kRomBank(r7, 0xa000);
      this.load8kRomBank(hardwired, 0xc000);
    } else {
      this.load8kRomBank(hardwired, 0x8000);
      this.load8kRomBank(r7, 0xa000);
      this.load8kRomBank(r6, 0xc000);
    }
    this.load8kRomBank(hardwired + 1, 0xe000);
  };

  // Re-derives all eight CHR windows from R0-R5 and the CHR map mode, using
  // the nesdev CHR table (mode 0: two 2 KB at $0000/$0800, four 1 KB at
  // $1000-$1C00; mode 1: the other way round).
  Mappers[4].prototype.updateChrBanks = function () {
    var s = this.bankSelect;
    // R0/R1 address 2 KB windows, so their low bit is not connected.
    var s0 = s[0] & 0xfe;
    var s1 = s[1] & 0xfe;

    if (this.chrAddressSelect === 0) {
      this.load1kVromBank(s0, 0x0000);
      this.load1kVromBank(s0 + 1, 0x0400);
      this.load1kVromBank(s1, 0x0800);
      this.load1kVromBank(s1 + 1, 0x0c00);
      this.load1kVromBank(s[2], 0x1000);
      this.load1kVromBank(s[3], 0x1400);
      this.load1kVromBank(s[4], 0x1800);
      this.load1kVromBank(s[5], 0x1c00);
    } else {
      this.load1kVromBank(s[2], 0x0000);
      this.load1kVromBank(s[3], 0x0400);
      this.load1kVromBank(s[4], 0x0800);
      this.load1kVromBank(s[5], 0x0c00);
      this.load1kVromBank(s0, 0x1000);
      this.load1kVromBank(s0 + 1, 0x1400);
      this.load1kVromBank(s1, 0x1800);
      this.load1kVromBank(s1 + 1, 0x1c00);
    }
  };

  Mappers[4].prototype.notifyA12 = function (level, lowDots) {
    this.lastA12 = level === 1;

    if (level === 1 && lowDots >= this.A12_FILTER_DOTS) {
      this.clockIrqCounter();
    }
  };

  // One filtered A12 rising edge: reload from the latch when the counter is
  // zero or a reload was requested, otherwise decrement; then, if the counter
  // is sitting at zero with interrupts enabled, raise the IRQ and stop until
  // the game re-arms with $E001. latch+1 edges between IRQs, per nesdev.
  Mappers[4].prototype.clockIrqCounter = function () {
    if (this.irqCounter === 0 || this.irqReload) {
      this.irqCounter = this.irqLatch;
      this.irqReload = false;
    } else {
      this.irqCounter--;
    }

    if (this.irqCounter === 0 && this.irqEnabled) {
      this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
      this.irqPending = true;
      this.irqEnabled = false;
    }
  };

  Mappers[4].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("MMC3: Invalid ROM! Unable to load.");
    }

    this.reset();

    // Power-on register values are unspecified on real hardware; this is the
    // usual starting point the games themselves assume: R6 at $8000 (bank 0),
    // R7 at $A000 (bank 1), second-to-last/last on top.
    this.bankSelect[6] = 0;
    this.bankSelect[7] = 1;
    this.updatePrgBanks();

    // Load CHR-ROM:
    this.loadCHRROM();

    // Load Battery RAM (if present) and the trainer:
    this.loadBatteryRam();
    this.loadTrainer();

    // Do Reset-Interrupt:
    this.resetIrqHandler();
  };

  Mappers[4].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.command = this.command;
    s.prgAddressSelect = this.prgAddressSelect;
    s.chrAddressSelect = this.chrAddressSelect;
    s.bankSelect = this.bankSelect.slice();
    s.irqCounter = this.irqCounter;
    s.irqLatch = this.irqLatch;
    s.irqReload = this.irqReload;
    s.irqEnabled = this.irqEnabled;
    s.irqPending = this.irqPending;
    s.prgRamEnabled = this.prgRamEnabled;
    s.prgRamWritable = this.prgRamWritable;
    s.lastA12 = this.lastA12;
    s.fourScreen = this.fourScreen;
    return s;
  };

  Mappers[4].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.command = s.command;
    this.prgAddressSelect = s.prgAddressSelect;
    this.chrAddressSelect = s.chrAddressSelect;
    this.bankSelect = s.bankSelect.slice();
    this.irqCounter = s.irqCounter;
    this.irqLatch = s.irqLatch;
    this.irqReload = s.irqReload;
    this.irqEnabled = s.irqEnabled;
    this.irqPending = s.irqPending;
    this.prgRamEnabled = s.prgRamEnabled;
    this.prgRamWritable = s.prgRamWritable;
    this.lastA12 = s.lastA12;
    this.fourScreen = s.fourScreen;
    if (this.fourScreen) {
      this.ownsMirroring = true;
    }
    // The CPU's copy of the ROM windows and the PPU's copy of the pattern
    // windows are not part of the mapper's state, so re-derive them from the
    // registers. Restoring only the registers would leave the previous game's
    // banks mapped.
    this.updateChrBanks();
    this.updatePrgBanks();
  };
}

module.exports = { register: register };
